import { supabase } from '../config/supabase';
import { v4 as uuidv4 } from 'uuid';
import slugify from '../utils/slugify';

export interface Craftsman {
  id?: number;
  store_name: string;
  owner_name: string;
  email: string;
  user_id?: string;
  phone_number: string;
  location: string;
  description: string;
  profession_id: number;
  avatar_url?: string;
  website?: string;
  show_map?: boolean;
  verified?: boolean;
  rating?: number;
  reviews?: number;
  slug?: string;
  created_at?: string;
  updated_at?: string;
  craft_professions?: {
    id: number;
    name: string;
    description: string;
    category: string;
  };
}

export const craftsmanService = {
  generateUniqueSlug: async (storeName: string, excludeId?: number | string): Promise<string> => {
    const base = slugify(storeName) || 'craftie';
    let candidate = base;
    let suffix = 2;

    while (true) {
      let query = supabase
        .from('crafties')
        .select('id')
        .eq('slug', candidate)
        .limit(1);

      if (excludeId !== undefined && excludeId !== null) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error checking craftie slug uniqueness:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return candidate;
      }

      candidate = `${base}-${suffix++}`;
    }
  },

  uploadAvatar: async (craftyId: number | string, file: File): Promise<string> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${craftyId}_${uuidv4()}.${fileExt}`;
      const filePath = `avatars/crafties/${craftyId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Error uploading craftie avatar:', uploadError);
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (!data || !data.publicUrl) {
        const urlError = new Error('Error getting craftie avatar URL: publicUrl is null');
        console.error(urlError.message);
        throw urlError;
      }

      return data.publicUrl;
    } catch (error) {
      console.error('Unexpected error in uploadAvatar (craftie):', error);
      throw error;
    }
  },

  async register(craftsman: Craftsman) {
    const slug = await craftsmanService.generateUniqueSlug(craftsman.store_name);

    const { data, error } = await supabase
      .from('crafties')
      .insert([{
        ...craftsman,
        rating: 0,
        reviews: 0,
        verified: false,
        slug
      }])
      .select()
      .single();

    if (error) throw error;

    // Trigger keyword generation for new crafties (non-blocking)
    if (data) {
      import('./keywordService')
        .then(({ triggerKeywordGeneration }) => {
          return triggerKeywordGeneration(data.id, 'craftie', {
            store_name: data.store_name,
            description: data.description,
            location: data.location,
          });
        })
        .catch((err) => {
          console.warn('Failed to trigger keyword generation for craftie:', err);
        });
    }

    return data;
  },

  async getAllCraftsmen() {
    console.log('Fetching all craftsmen...');
    const { data, error } = await supabase
      .from('crafties')
      .select(`
        *,
        craft_professions (
          id,
          name,
          description,
          category
        )
      `);
    // Removed .eq('verified', true) for debugging

    if (error) {
      console.error('Error fetching craftsmen:', error);
      throw error;
    }
    
    console.log('Fetched craftsmen data:', data);
    return data;
  },

  async getCraftsmanByEmail(email: string) {
    const { data, error } = await supabase
      .from('crafties')
      .select(`
        *,
        craft_professions (
          id,
          name,
          description,
          category
        )
      `)
      .eq('email', email)
      .single();

    if (error) throw error;
    return data;
  },

  async updateCraftsman(id: number, updates: Partial<Craftsman>) {
    const updatesWithSlug = { ...updates };

    if (updates.store_name) {
      updatesWithSlug.slug = await craftsmanService.generateUniqueSlug(updates.store_name, id);
    }

    const { data, error } = await supabase
      .from('crafties')
      .update(updatesWithSlug)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Trigger keyword regeneration when key fields change (non-blocking)
    const keywordRelevantFields: Array<keyof Craftsman> = ['description', 'store_name', 'location'];
    const updatedKeywordFields = keywordRelevantFields.some((field) => updates[field] !== undefined);

    if (updatedKeywordFields && data) {
      import('./keywordService')
        .then(({ triggerKeywordGeneration }) => {
          return triggerKeywordGeneration(data.id, 'craftie', {
            store_name: data.store_name,
            description: data.description,
            location: data.location,
          });
        })
        .catch((err) => {
          console.warn('Failed to trigger keyword regeneration for craftie:', err);
        });
    }

    return data;
  },

  async listVerifiedCraftsmen() {
    console.log('Fetching verified and mockup craftsmen...');
    const { data, error } = await supabase
      .from('crafties')
      .select(`
        *,
        craft_professions (
          id,
          name,
          description,
          category
        )
      `)
      .or('verified.eq.true,mockup.eq.true')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching verified craftsmen:', error);
      throw error;
    }
    
    console.log('Fetched verified craftsmen data:', data);
    return data;
  },

  async getCraftsmanBySlug(slug: string): Promise<Craftsman | null> {
    if (!slug) {
      return null;
    }

    const { data, error } = await supabase
      .from('crafties')
      .select(`
        *,
        craft_professions (
          id,
          name,
          description,
          category
        )
      `)
      .eq('slug', slug)
      .single();

    if (error) {
      console.error('Error fetching craftsman by slug:', error);
      return null;
    }

    return data;
  },

  async getCraftsmanById(id: number | string): Promise<Craftsman | null> {
    const { data, error } = await supabase
      .from('crafties')
      .select(`
        *,
        craft_professions (
          id,
          name,
          description,
          category
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching craftsman by id:', error);
      return null;
    }

    return data;
  },

  async getCraftsmanByUserId(userId: string): Promise<Craftsman | null> {
    const { data, error } = await supabase
      .from('crafties')
      .select(`
        *,
        craft_professions (
          id,
          name,
          description,
          category
        )
      `)
      .eq('user_id', userId)
      .single();

    if (error) {
      if ((error as any).code === 'PGRST116') {
        console.log('No craftsman found for user ID (likely non-craftie account):', userId);
        return null;
      }

      console.error('Error fetching craftsman by user ID:', error);
      return null;
    }

    return data;
  }
};

export default craftsmanService;
